import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatFormField, MatInput, MatLabel, MatError } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { ErrorStateMatcher, MatOption } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { Modal } from '@spirit-testing/component-library';
import { CustomerService } from '../../services/customer.service';
import {
  AuthMethod,
  CreateCustomerRequest,
  CustomerStatus,
  HostingMode,
  UpdateCustomerRequest,
} from '../../models/customer.model';
import { StatusPillComponent, customerStatusKind } from '../../components/status-pill/status-pill.component';
import { SectionTitleComponent } from '../../components/section-title/section-title.component';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { createFormDirtyTracker } from '../../shared/forms/dirty-tracker';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';

const AUTH_METHODS: readonly AuthMethod[] = ['ENTRA', 'OTP', 'BOTH'];
const ARROW_KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
const AUTH_I18N = 'clientManagement.customerForm.authAccess.';

function otpRequiresDomain(group: AbstractControl): ValidationErrors | null {
  const method = group.get('authMethod')?.value as AuthMethod;
  const domains = (group.get('allowedDomains')?.value ?? []) as string[];
  return method !== 'ENTRA' && !domains.some((d) => d.trim()) ? { otpRequiresDomain: true } : null;
}

@Component({
  selector: 'app-customer-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DatePipe,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    MatProgressSpinner,
    TranslatePipe,
    StatusPillComponent,
    SectionTitleComponent,
    Modal,
  ],
  templateUrl: './customer-form.component.html',
  styleUrl: './customer-form.component.scss',
})
export class CustomerFormComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly customerService = inject(CustomerService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly saveError = signal<string | null>(null);
  readonly showDeactivateConfirm = signal(false);
  readonly authChangeWarning = signal<string | null>(null);
  readonly statusKind = customerStatusKind;
  readonly authMethods = AUTH_METHODS;

  private customerId: string | null = null;
  private savedAuthMethod: AuthMethod = 'ENTRA';
  private readonly deactivateConfirm$ = new Subject<boolean>();

  get isEdit(): boolean {
    return !!this.customerId;
  }

  readonly fieldLengths = FIELD_LENGTHS;

  protected readonly grunddatenHeadingId = 'customer-form-grunddaten-heading';
  protected readonly hostingHeadingId = 'customer-form-hosting-heading';
  protected readonly authHeadingId = 'customer-form-auth-heading';
  protected readonly authMethodLabelId = 'customer-form-auth-method-label';
  protected readonly domainsErrorId = 'customer-form-domains-error';
  protected readonly vertragHeadingId = 'customer-form-vertrag-heading';

  readonly form = this.fb.group(
    {
      name: ['', [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
      domain: ['', [Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
      contactPerson: ['', [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
      hostingMode: ['' as HostingMode | '', [Validators.required]],
      azureTenantId: ['', [Validators.required, Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]],
      contractStart: this.fb.control<Date | null>(null),
      contractEnd: this.fb.control<Date | null>(null),
      status: ['' as CustomerStatus | '', [Validators.required]],
      authMethod: this.fb.control<AuthMethod>('ENTRA'),
      allowedDomains: this.fb.array([this.fb.control('')]),
    },
    { validators: otpRequiresDomain },
  );

  private readonly dirtyTracker = createFormDirtyTracker(this.form);
  readonly isDirty = this.dirtyTracker.isDirty;

  protected readonly domainErrorMatcher: ErrorStateMatcher = {
    isErrorState: (control) => this.hasDomainsError() || (!!control?.invalid && control.touched),
  };

  protected domainsErrorRef(): string | null {
    return this.hasDomainsError() ? this.domainsErrorId : null;
  }

  private hasDomainsError(): boolean {
    return this.form.hasError('otpRequiresDomain');
  }

  saveDisabled(): boolean {
    return this.form.invalid || this.saving() || (this.isEdit && !this.isDirty());
  }

  get initials(): string {
    const name = this.form.value.name ?? '';
    if (!name.trim()) return '';
    return name
      .split(' ')
      .map((s) => s[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
  }

  ngOnInit(): void {
    this.customerId = this.activatedRoute.snapshot.paramMap.get('customerId');

    if (this.isEdit && this.customerId) {
      this.loading.set(true);
      this.customerService.getById(this.customerId).subscribe({
        next: (customer) => {
          this.form.patchValue({
            name: customer.name,
            domain: customer.domain ?? '',
            contactPerson: customer.contactPerson ?? '',
            hostingMode: customer.hostingMode,
            azureTenantId: customer.azureTenantId ?? '',
            contractStart: this.parseIsoDate(customer.contractStart),
            contractEnd: this.parseIsoDate(customer.contractEnd),
            status: customer.status,
            authMethod: customer.authMethod ?? 'ENTRA',
          });
          this.savedAuthMethod = customer.authMethod ?? 'ENTRA';
          this.setDomains(customer.allowedDomains ?? []);
          this.dirtyTracker.reset();
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set('clientManagement.customerForm.loadError');
          this.loading.set(false);
        },
      });
    } else {
      this.form.controls.status.setValue('ONBOARDING' as CustomerStatus);
      this.dirtyTracker.reset();
    }
  }

  hasUnsavedChanges(): boolean {
    return this.isDirty() && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  selectAuthMethod(method: AuthMethod): void {
    this.form.controls.authMethod.setValue(method);
  }

  onAuthMethodKeydown(event: KeyboardEvent, method: AuthMethod): void {
    if (event.key === ' ') {
      event.preventDefault();
      this.selectAuthMethod(method);
      return;
    }
    if (!ARROW_KEYS.includes(event.key)) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    const next = (AUTH_METHODS.indexOf(method) + step + AUTH_METHODS.length) % AUTH_METHODS.length;
    this.selectAuthMethod(AUTH_METHODS[next]);
    const radios = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLElement>('[role="radio"]');
    radios?.[next]?.focus();
  }

  addDomain(): void {
    this.form.controls.allowedDomains.push(this.fb.control(''));
  }

  removeDomain(index: number): void {
    const domains = this.form.controls.allowedDomains;
    if (domains.length > 1) {
      domains.removeAt(index);
    } else {
      domains.at(0).setValue('');
    }
  }

  onAuthChangeConfirmed(): void {
    this.authChangeWarning.set(null);
    this.submit();
  }

  onAuthChangeCancelled(): void {
    this.authChangeWarning.set(null);
  }

  onSave(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving()) return;

    const warning = this.isEdit ? this.authChangeWarningFor(this.form.controls.authMethod.value) : null;
    if (warning) {
      this.authChangeWarning.set(warning);
      return;
    }
    this.submit();
  }

  onCancel(): void {
    this.navigateBack();
  }

  private submit(): void {
    this.saving.set(true);
    this.saveError.set(null);

    if (this.isEdit && this.customerId) {
      this.saveUpdate(this.customerId);
    } else {
      this.saveCreate();
    }
  }

  private saveCreate(): void {
    const { name, domain, contactPerson, hostingMode, azureTenantId, contractStart, contractEnd, status } =
      this.form.getRawValue();
    const request: CreateCustomerRequest = {
      name,
      azureTenantId,
      hostingMode: hostingMode as HostingMode,
      status: status ? (status as CustomerStatus) : undefined,
      domain: domain || undefined,
      contactPerson: contactPerson || undefined,
      contractStart: this.formatDate(contractStart),
      contractEnd: this.formatDate(contractEnd),
      ...this.authPayload(),
    };
    this.customerService.createCustomer(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.dirtyTracker.reset();
        this.snackBar.open(this.translate.instant('clientManagement.customerForm.createSuccess'));
        this.navigateBack();
      },
      error: (err: unknown) => this.onSaveFailed(err),
    });
  }

  private saveUpdate(customerId: string): void {
    const { name, domain, contactPerson, azureTenantId, contractStart, contractEnd, status } = this.form.getRawValue();
    const request: UpdateCustomerRequest = {
      name,
      azureTenantId: azureTenantId || undefined,
      status: status as CustomerStatus,
      domain: domain || undefined,
      contactPerson: contactPerson || undefined,
      contractStart: this.formatDate(contractStart),
      contractEnd: this.formatDate(contractEnd),
      ...this.authPayload(),
    };
    this.customerService.updateCustomer(customerId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.dirtyTracker.reset();
        this.snackBar.open(this.translate.instant('clientManagement.customerForm.saveSuccess'));
        this.navigateBack();
      },
      error: (err: unknown) => this.onSaveFailed(err),
    });
  }

  private authPayload(): Pick<UpdateCustomerRequest, 'authMethod' | 'allowedDomains'> {
    return {
      authMethod: this.form.controls.authMethod.value,
      allowedDomains: this.form.controls.allowedDomains.getRawValue().filter((d) => d.trim() !== ''),
    };
  }

  private onSaveFailed(err: unknown): void {
    this.saving.set(false);
    if (!this.showAuthError(err)) {
      this.saveError.set('clientManagement.customerForm.saveError');
    }
  }

  private showAuthError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    const { code, domain, customerName } = (err.error ?? {}) as Record<string, string | undefined>;
    if (code === 'OTP_REQUIRES_ALLOWED_DOMAIN') {
      this.form.setErrors({ otpRequiresDomain: true });
      return true;
    }
    const errorKey =
      code === 'ALLOWED_DOMAIN_TAKEN' ? 'domainTaken' : code === 'INVALID_ALLOWED_DOMAIN' ? 'invalidDomain' : null;
    const row = this.form.controls.allowedDomains.controls.find((c) => c.value.trim().toLowerCase() === domain);
    if (!errorKey || !row) return false;
    row.setErrors({ [errorKey]: { customerName } });
    row.markAsTouched();
    return true;
  }

  private authChangeWarningFor(next: AuthMethod): string | null {
    if (this.savedAuthMethod !== 'ENTRA' && next === 'ENTRA') return AUTH_I18N + 'confirm.otpLoss';
    if (this.savedAuthMethod !== 'OTP' && next === 'OTP') return AUTH_I18N + 'confirm.entraLoss';
    return null;
  }

  private setDomains(domains: string[]): void {
    const controls = this.form.controls.allowedDomains;
    controls.clear();
    for (const domain of domains.length ? domains : ['']) {
      controls.push(this.fb.control(domain));
    }
  }

  private navigateBack(): void {
    this.router.navigate(['/client-management']);
  }

  private parseIsoDate(value: string | undefined | null): Date | null {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return null;
    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatDate(value: Date | null): string | undefined {
    if (!value) return undefined;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
